<template>
  <v-container>
    <div>
      <settings-bar
        v-if="!isReadOnly"
        :parent="'pathManagement'"
        :pathId="pathId"
        :areDraftMissions="draftMissions"
        :areAllMissionsValid="areAllMissionsValid"
        :isPathFormComplete="isValidPathForm"
        :checkListProgression="checkListProgression"
        :pathInfos="pathInfos"
        :accountInfos="accountInfos"
        :missionsInfos="missionsInfos"
        :checkListElements="checkListElements"
        :width="'330'"
        @test-translation="checkTranslationIsComplete('en')"
        @openChecklist="isCheckListOpen = !isCheckListOpen"
        @change-lang-edition="changeLangEdition"
        @approve-path="approvePath"
        @langs-path-to-translate="addLangToPath"
        @add-lang-published="addLangPublished"
        @unpublish-lang="unpublishLang"
        @update-path="updatePath"
        @delete-path-lang="deletePathLang"
      />
    </div>

    <!-- sticky show trad-->
    <div
      class="stickyClass"
      v-if="
        langEdition !== locale && pathInformations.langsEdited.includes(locale)
      "
    >
      <v-flex layout column align-center>
        <!--v-flex layout mt-2 class="hintInfo">
          <span>
            Edition en {{$t('languages.'+langEdition)}}
          </span>
        </v-flex-->
        <v-tooltip top>
          <template v-slot:activator="{ on: on }">
            <v-btn
              small
              color="white"
              class="text-none primary--text"
              @click="changeHint()"
              v-on="on"
            >
              <v-icon color="primary" size="18" class="mr-2" v-if="showHint">
                mdi-eye-outline
              </v-icon>
              <v-icon color="primary" size="18" class="mr-2" v-else>
                mdi-eye-off-outline
              </v-icon>
              <span>
                {{ $t('languages.' + locale) }}
              </span>
            </v-btn>
          </template>
          <span v-if="showHint">
            {{ $tc('languageHint.hide', $t('languages.' + locale)) }}
          </span>
          <span v-else>
            {{ $tc('languageHint.show', $t('languages.' + locale)) }}
          </span>
        </v-tooltip>
      </v-flex>
    </div>

    <v-layout justify-center>

      <v-form ref="pathForm">

        <!-- unique codes -->
        <v-card-title v-if="pathInformations.uniqueCodeCount" class="justify-center cardWidth">
          <h2 class="font-weight-light" style="color:red;">
            {{ $t('pathManagement.uniqueCodesLeft') }}: {{ pathInformations.uniqueCodeCount }}
          </h2>
        </v-card-title>

        <v-card-title class="justify-center cardWidth">
          <h2 class="font-weight-light">
            {{ $t('pathManagement.information') }}
          </h2>
        </v-card-title>

        <!-- general path form -->
        <path-form
          class="cardWidth"
          :pathId="pathId"
          :langEdition="langEdition"
          :showHint="showHint"
          :isReadOnly="isReadOnly"
          @is-valid-path-form="updateValidPathForm"
        >
        </path-form>

        <v-card-title v-if="isReadOnly" class="justify-center cardWidth">
          <v-icon x-large>mdi-qrcode-scan</v-icon>
          <h2 class="font-weight-light">
            {{ $t('pathManagement.seeInApp') }}
          </h2>
        </v-card-title>
        <v-flex
          layout
          align-center
          justify-center
          column
          pa-4
          v-if="isReadOnly"
        >
          <img :src="getQrcodeUrl()" alt="qrcode" class="qrcode-small" />
        </v-flex>

        <!-- general path options -->
        <v-card class="cardWidth mt-4" @click="$router.push(`/pathSettings/${pathId}`)">
          <v-flex justify-center class="grey--text">
            {{ $t('managePath.options') }}
          </v-flex>
          <v-chip outline color="primary" v-if="pathInformations.isGuided" >{{$t('managePath.optionChip.guidedMode') }}</v-chip>
          <v-chip outline color="primary" 
            v-if="pathInformations.restriction && 
            (pathInformations.restriction.type ==='password' || pathInformations.restriction.type ==='qrcode')" >
            <v-icon >mdi-lock</v-icon>
            {{$t('managePath.optionChip.restrictionPrivate')}}
          </v-chip>
          <v-chip outline color="primary" 
            v-else>
            <v-icon >mdi-lock-open</v-icon>
            {{$t('managePath.optionChip.restrictionPublic')}}
          </v-chip>

          <v-chip v-if="pathInformations.colorPrimary" :color="pathInformations.colorPrimary">{{$t('managePath.optionChip.color1')}}</v-chip>
          <v-chip v-if="pathInformations.colorSecondary" :color="pathInformations.colorSecondary">{{$t('managePath.optionChip.color2')}}</v-chip>
          <v-chip outline color="primary" v-if="pathInformations.cardTextureUrl">
            <v-avatar>
              <img :src="pathInformations.cardTextureUrl" alt="texture">
            </v-avatar>
            {{$t('managePath.optionChip.cardTexture')}}
          </v-chip>
          <v-chip outline color="primary" v-if="pathInformations.randomStart" >{{$t('managePath.optionChip.randomStart')}}</v-chip>
          <v-chip outline color="primary" v-else-if="pathInformations.randomOrder" >{{$t('managePath.optionChip.randomOrder')}}</v-chip>
          <v-chip outline color="primary" v-if="pathInformations.lockMissions" >{{$t('managePath.optionChip.lockMissions')}}</v-chip>
          <v-chip outline color="primary" v-if="pathInformations.mapPictureUrl">
            <v-avatar>
              <img :src="pathInformations.mapPictureUrl" alt="Map">
            </v-avatar>
            {{$t('managePath.optionChip.mapPicture')}}
          </v-chip>
          <v-chip outline color="primary" v-else-if="pathInformations.isMapAvailable" >{{$t('managePath.optionChip.mapAvailable')}}</v-chip>
          <v-chip outline color="primary" v-if="pathInformations.trace && pathInformations.trace.coordinates" >{{$t('managePath.optionChip.trace')}}</v-chip>
          <v-chip outline color="primary" v-if="pathInformations.isRestrictedToAdults" >{{$t('managePath.optionChip.restrictedToAdults')}}</v-chip>
        </v-card>

        <v-card-title class="justify-center cardWidth">
          <h2 class="font-weight-light">
            {{ $t('pathManagement.chaining') }}
          </h2>
        </v-card-title>

        

        <!-- path intro -->
        <v-card class="cardWidth">
          <v-tooltip top>
            <template v-slot:activator="{ on: on }">
              <div class="grey--text" v-on="on">
                {{ $t('pathManagement.introduction') }}
              </div>
              <div>
                <v-textarea
                  v-on="on"
                  solo
                  flat
                  no-resize
                  rows="3"
                  :disabled="isReadOnly"
                  :counter="maxCharIntro"
                  :rules="getIntroductionRules()"
                  v-model="pathIntroduction[langEdition]"
                  :placeholder="$t('pathManagement.placeholder.introduction')"
                  @blur="updateIntroduction"
                  :persistent-hint="showHint"
                  :hint="
                    langEdition !== locale ? pathIntroduction[locale] : null
                  "
                >
                  <language-translator
                    v-if="locale !== langEdition"
                    :langEdition="langEdition"
                    :textToTranslate="pathIntroduction[locale]"
                    :type="'introduction'"
                    @text-translated="updateTranslatedText"
                    slot="append"
                  >
                  </language-translator>
                </v-textarea>
                <media-input
                  v-if="!isReadOnly"
                  :label="$t('pathForm.introductionMedia')"
                  :default="pathMediaIntroduction.url"
                  :type="pathMediaIntroduction.type"
                  :name="pathMediaIntroduction.name"
                  @change-media-input="updateIntroductionMedia"
                  @uploading-media="uploadingMedia"
                ></media-input>
              </div>
            </template>
            <span color="primary">{{
              $t('pathManagement.tooltips.introduction')
            }}</span>
          </v-tooltip>
        </v-card>


         <!-- fixed first mission -->
        <mission-card
            v-if="firstMission && firstMission.isFixed"
            v-for="(mission, key) of [firstMission]"
            :key="0"
            class="cardWidth mt-4 mb-4"
            :mission="getRawMission(mission.missionId)"
            :pathMission="mission"
            :pathId="pathId"
            :indexKey="0"
            :langEdition="langEdition"
            :showHint="showHint"
            :isReadOnly="isReadOnly"
            @copyMissionToPath="copyMissionToPath"
            @onPublishSwitch="publishMission(mission.missionId, ...arguments)"
            @onRemove="removeMission"
            @uploading-media="uploadingMedia"
          >
          </mission-card>
        <!-- missions list -->
        <draggable
          :disabled="!isDragButtonClicked"
          :scrollSensitivity="190"
          :force-fallback="true"
          v-model="pathMissions"
          @start="startDrag"
          @end="endDrag"
        >
          <mission-card
            v-for="(mission, key) of pathMissions.filter(m => !m.isFixed)"
            :key="firstMission.isFixed ? key+1 : key"
            class="cardWidth mt-4 mb-4"
            :mission="getRawMission(mission.missionId)"
            :pathMission="mission"
            :pathId="pathId"
            :indexKey="firstMission.isFixed ? key+1 : key"
            :isDrag="drag"
            :langEdition="langEdition"
            :showHint="showHint"
            :isReadOnly="isReadOnly"
            @copyMissionToPath="copyMissionToPath"
            @drag-button-click="dragButtonClick"
            @onPublishSwitch="publishMission(mission.missionId, ...arguments)"
            @onRemove="removeMission"
            @uploading-media="uploadingMedia"
          >
          </mission-card>
        </draggable>

        <!-- fixed end mission -->
        <mission-card
            v-if="lastMission && lastMission.isFixed"
            v-for="(mission, key) of [lastMission]"
            :key="pathMissions.length-1"
            class="cardWidth mt-4 mb-4"
            :mission="getRawMission(mission.missionId)"
            :pathMission="mission"
            :pathId="pathId"
            :indexKey="pathMissions.length-1"
            :langEdition="langEdition"
            :showHint="showHint"
            :isReadOnly="isReadOnly"
            @copyMissionToPath="copyMissionToPath"
            @onPublishSwitch="publishMission(mission.missionId, ...arguments)"
            @onRemove="removeMission"
            @uploading-media="uploadingMedia"
          >
          </mission-card>

        <v-layout justify-center>
          <v-card
            class="addCard pointerClass"
            width="200px"
            color="primary"
            @click="isReadOnly ? '' : openMissionPicker()"
          >
            <v-flex
              layout
              justify-center
              align-center
              row
              pr-2
              pt-2
              pb-2
              color="white"
            >
              <v-icon medium color="white" class="mr-2"
                >mdi-plus-circle-outline</v-icon
              >
              <span class="white--text" style="text-align: center">
                {{ $t('pathManagement.missionPicker.addMissions') }}
              </span>
            </v-flex>
          </v-card>
        </v-layout>

        <!-- path conclusion -->
        <v-card class="cardWidth">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <div class="grey--text" v-on="on">
                {{ $t('pathManagement.conclusion') }}
              </div>
              <div>
                <v-textarea
                  v-on="on"
                  no-resize
                  solo
                  flat
                  :counter="maxCharIntro"
                  :disabled="isReadOnly"
                  rows="3"
                  :rules="getConclusionRules()"
                  v-model="pathConclusion[langEdition]"
                  :placeholder="$t('pathManagement.placeholder.conclusion')"
                  @blur="updateConclusion"
                  :persistent-hint="showHint"
                  :hint="langEdition !== locale ? pathConclusion[locale] : null"
                >
                  <language-translator
                    v-if="locale !== langEdition"
                    :langEdition="langEdition"
                    :textToTranslate="pathConclusion[locale]"
                    :type="'conclusion'"
                    @text-translated="updateTranslatedText"
                    slot="append"
                  >
                  </language-translator>
                </v-textarea>
                <media-input
                  v-if="!isReadOnly"
                  :label="$t('pathForm.conclusionMedia')"
                  :default="pathMediaConclusion.url"
                  :type="pathMediaConclusion.type"
                  :name="pathMediaConclusion.name"
                  @change-media-input="updateConclusionMedia"
                  @uploading-media="uploadingMedia"
                ></media-input>
              </div>
            </template>
            <span color="primary">{{
              $t('pathManagement.tooltips.conclusion')
            }}</span>
          </v-tooltip>
        </v-card>
      </v-form>
    </v-layout>

    <!-- checklist dialog -->
    <v-dialog
      v-model="isCheckListOpen"
      width="40%"
      max-width="700px"
      min-height="90%"
      lazy
    >
      <v-card>
        <check-list
          :pathInfos="pathInfos"
          :accountInfos="accountInfos"
          :missionsInfos="missionsInfos"
          :checkListElements="checkListElements"
          :checkListPreviewMode="false"
          :pathId="pathId"
          style="margin: auto"
        />
        <!-- <v-flex layout row pl-4 pr-4 pt-2>
          <v-spacer/>
          <v-btn color="primary" class="btnWidth btnHeight text-none"
          @click="isCheckListOpen = false">
            <span>Fermer</span>
          </v-btn>
        </v-flex> -->
      </v-card>
    </v-dialog>

    <!-- mission picker dialog -->
    <v-dialog
      v-model="isMissionPickerOpen"
      width="80%"
      max-width="900px"
      min-height="90%"
      persistent
      lazy
    >
      <v-card>
        <v-flex layout align-center justify-center>
          <v-card-title
            class="headline"
            style="padding-top: 10px; padding-bottom: 10px"
            primary-title
          >
            {{ $t('pathManagement.missionPicker.name') }}
          </v-card-title>
        </v-flex>
        <v-divider />
        <mission-picker
          :missions="missions"
          :langEdition="langEdition"
          :parent="'pathManagement'"
          :numberMissions="numberMissionsInPath"
          :isMissionPickerOpen="isMissionPickerOpen"
          @update-missions-to-add="updateMissionsToAdd"
        />
        <v-flex layout row pl-4 pr-4 pt-2>
          <v-btn
            color="primary"
            outline
            class="btnWidth text-none"
            @click="closeMissionPicker()"
          >
            <span>{{ $t('pathManagement.missionPicker.cancel') }}</span>
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            @click="newMission"
            class="btnWidth text-none"
            :disabled="missionsToAdd.length > 0"
          >
            <v-icon color="white" class="mr-2" medium
              >mdi-plus-circle-outline</v-icon
            >
            <span>{{ $t('pathManagement.missionPicker.create') }}</span>
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            class="btnWidth text-none"
            @click="addMissions"
            :disabled="missionsToAdd.length < 1"
          >
            <span>{{
              $tc('pathManagement.missionPicker.add', missionsToAdd.length)
            }}</span>
          </v-btn>
        </v-flex>
      </v-card>
    </v-dialog>

    <!-- media loading dialog-->
    <load-dialog :isUploading="isUploading" :percentProgress="percentProgress">
    </load-dialog>
  </v-container>
</template>

<script>
import SettingsBar from '../components/SettingsBar.vue';
import MissionCard from '../components/MissionCard';
import MissionPicker from '../components/MissionPicker';
import PathForm from '../components/PathForm';
import MediaInput from '../components/MediaInput';
import Draggable from 'vuedraggable';
import LanguageTranslator from '../components/LanguageTranslator.vue';
import CheckList from '../components/CheckList.vue';
import LoadDialog from '../components/LoadDialog.vue';

export default {
  async beforeRouteLeave(to, from, next) {
    if (this.pathInformations.isRealtime) {
      try {
        await this.$store.dispatch('players/UNSUBSCRIBE', this.$kuzzle);
      } catch (error) {
        console.error(error.message);
      }
    }

    next();
  },
  name: 'PathManagement',
  components: {
    SettingsBar,
    MissionCard,
    MissionPicker,
    PathForm,
    Draggable,
    LanguageTranslator,
    MediaInput,
    CheckList,
    LoadDialog,
  },
  data() {
    return {
      langEdition: null,
      originLangBeforeTranslation: '',
      isMissionPickerOpen: false,
      deleteLastMissionConfirmation: false,
      pathIntroduction: {},
      pathMediaIntroduction: {},
      pathMediaConclusion: {},
      pathConclusion: {},
      pathIsPublished: null,
      publishConfirmation: false,
      drag: false,
      isDragButtonClicked: false,
      isUploading: false,
      percentProgress: 0,
      missionsToAdd: [],
      isValidPathForm: false,
      showHint: false,
      isReadOnly: false,
      maxCharIntro: 300,
      isCheckListOpen: false,
      counter: 0,
      articlesSuggest: [
        '6316233ec713d51da3edb94d', //concevoir un parcours
        '6328addb62b5f93d1801882a', // publier / dépublier
        '605b22d73f70ab34d9ba27f4', // tester
      ],
      articlesTitles: [
        {
          fr: 'Concevoir un parcours',
          en: 'Design a path',
        },
        {
          fr: 'Comment publier / dépublier mon parcours',
          en: 'How to publish / unpublish my path',
        },
        {
          fr: 'Comment tester mon parcours',
          en: 'How to test my path',
        },
      ],
      // pathInfos: {},
      // missionsInfos: {}
    };
  },
  // mounted() {
  //   this.missionsInfos['3mission'] = this.numberMissionsInPath > 2 ? true : false
  //   this.missionsInfos['mission'] = this.numberMissionsInPath > 0 ? true : false
  //   this.pathInfos['name'] = this.pathInformations.name[this.langEdition].length > 0 ? true : false
  //   this.pathInfos['img'] = this.pathInformations.pictures.length > 0 ? true : false
  //   this.pathInfos['conclusion'] = this.pathConclusion[this.langEdition].length > 0 ? true : false
  //   this.pathInfos['translate'] = this.pathInformations.langsEdited.length > 1 ? true : false
  //   this.pathInfos['intro'] = this.pathIntroduction[this.langEdition].length > 0 ? true : false
  // },
  watch: {
    pathId(newValue) {
      return newValue;
    },
    isValidPathForm(newValue) {
      return newValue;
    },
    pathMissions(newValue) {
      return newValue;
    },
    langEdition(newValue) {
      this.$refs.pathForm.resetValidation();
      return newValue;
    },
    pathInformations(newValue) {
      this.setPathValues();
      return newValue;
    },
  },

  computed: {
    // JCS changer les noms
    // revoir condiitons initiales
    pathInfos() {
      let pathInfo = {};
      pathInfo.name =
        this.pathInformations.name[this.langEdition].length > 0 ? true : false;
      pathInfo.img =
        this.pathInformations.pictures && this.pathInformations.pictures[0].url
          ? true
          : false;
      pathInfo.conclusion =
        this.pathConclusion[this.langEdition] &&
        this.pathConclusion[this.langEdition].length > 0
          ? true
          : false;
      if (this.access.includes('lang')) {
        pathInfo.translate =
          this.pathInformations.langsEdited.length > 1 ? true : false;
      }
      pathInfo.intro =
        this.pathIntroduction[this.langEdition] &&
        this.pathIntroduction[this.langEdition].length > 0
          ? true
          : false;

      return pathInfo;
    },
    missionsInfos() {
      let missionInfo = {};

      missionInfo['3mission'] = this.numberMissionsInPath > 2 ? true : false;
      missionInfo.mission = this.numberMissionsInPath > 0 ? true : false;

      return missionInfo;
    },
    accountInfos() {
      let accountInfo = {};

      accountInfo.logo = this.currentAccount.logoUrl ? true : false;
      // accountInfo['socials'] = false;

      return accountInfo;
    },
    checkListProgression() {
      var count = 0;

      count += Object.values(this.accountInfos).reduce(
        (a, item) => a + item,
        0
      );
      count += Object.values(this.missionsInfos).reduce(
        (a, item) => a + item,
        0
      );
      count += Object.values(this.pathInfos).reduce((a, item) => a + item, 0);
      count += this.checkListElements ? this.checkListElements.length : 0;

      return Math.ceil(
        (100 * count) / (this.access.includes('lang') ? 12 : 11)
      );
    },
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    checkListElements() {
      return this.pathInformations.checklist || [];
    },
    currentAccount() {
      return this.$store.getters['accounts/currentAccount'];
    },
    missions() {
      const { missions } = this.$store.state.missions;
      return Object.keys(missions).map((key) => ({
        key,
        ...missions[key],
      }));
    },
    draftMissions() {
      let missionsIds = this.pathMissions.map((mission) => mission.missionId);
      return missionsIds.filter(
        (missionId) =>
          this.getRawMission(missionId).isComplete &&
          this.getRawMission(missionId).isComplete[this.langEdition] === false
      );
    },
    locale() {
      return this.$i18n.locale;
    },
    pathInformations() {
      return this.$store.getters['paths/getPathById'](this.pathId);
    },
    pathId() {
      return this.$route.params.pathId;
    },
    missingMissions() {
      const missionIds = this.pathInformations.missions.map(
        (mission) => mission.missionId
      );
      return missionIds.filter((missionId) => !this.getRawMission(missionId));
    },
    missingMissionsLength() {
      return this.missingMissions.length;
    },
    pathMissions: {
      get() {
        return this.pathInformations.missions.filter(
          (mission) => !this.missingMissions.includes(mission.missionId)
        );
      },
      async set(value) {
        let pathPayload = {}
        if (this.firstMission.isFixed) {
          // model PathMissions is filtered by isFixed
          // so the returned 'value' has index offset, ids are not right
          // need to find which is the real dragged element before update
          const missionsList = this.pathMissions.map(m=>m.missionId)
          const moves = this.findArrayMovements(missionsList, value.map(m=>m.missionId)); 
          console.log('moves', moves)
          // find element that was moved
          const movedElInArray = moves.find(e => Math.abs(e.difference) === moves.length - 1)
          console.log('moved element', movedElInArray)
          // find element that was really dragged by user
          // apply index +1 offset
          const realMovedElIndex= missionsList.findIndex(e=>e === movedElInArray.id) +1
          const realMovedEl= missionsList[realMovedElIndex]
          console.log('real element id', realMovedEl)

          const newPathMissions = this.moveElementInArray(
            JSON.parse(JSON.stringify(this.pathMissions)),
            realMovedElIndex, 
            movedElInArray.difference
          )
          console.log('result', newPathMissions.map(m=>m.missionId))
          pathPayload = { missions: newPathMissions }
        } else {
          pathPayload = { missions: value }
        }
        
        await this.$store.commit('app/SET_LOADING');
        try {
          await this.$store.dispatch('paths/UPDATE_PATH', {
            pathId: this.pathId,
            pathPayload: pathPayload,
            index: 'clients',
            kuzzle: this.$kuzzle,
          });
        } catch (error) {
          console.error(error.message);
        }
        await this.$store.commit('app/UNSET_LOADING');
      },
    },
    firstMission() {
      return this.pathMissions[0]
    },
    lastMission() {
      return this.pathMissions[this.pathMissions.length-1]
    },
    numberMissionsInPath() {
      if (this.pathMissions) {
        return this.pathMissions.length;
      }
      return 0;
    },
    areAllMissionsValid() {
      return this.draftMissions.length === 0;
    },
    idsMissionsInPath() {
      let ids = [];
      if (this.pathMissions) {
        ids = this.pathMissions.map((mission) => mission.missionId);
      }
      return ids;
    },
    offer() {
      // return this.$store.getters['accounts/currentAccount'].offer ||
      return {};
    },
    access() {
      return (
        this.$store.getters['paths/getPathsAccess'][
          this.$route.params.pathId
        ] || []
      );
    },
  },
  created() {
    this.langEdition = this.locale;
    this.setPathValues();
    window.Beacon('suggest', this.articlesSuggest);
    this.switchHelpScoutText();
  },
  methods: {
    findArrayMovements(arr1, arr2) {
      let differences = [];

      // Parcourir chaque élément de arr1
      arr1.forEach((element, index) => {
        // Trouver l'index de l'élément dans arr2
        let newIndex = arr2.indexOf(element);
        
        // Calculer la différence d'index
        let diff = newIndex - index;

        // Ajouter la différence au tableau des résultats
        if (diff !== 0) { // Si l'élément a changé de position
          differences.push({
            id: element,
            difference: diff
          });
        }
      });

      return differences;
    },
    moveElementInArray(arr, index, moveBy) {
      // Calculer la nouvelle position en fonction de moveBy
      let newIndex = index + moveBy;

      // S'assurer que l'index ne sorte pas des limites du tableau
      if (newIndex < 0) {
        newIndex = 0;
      } else if (newIndex >= arr.length) {
        newIndex = arr.length - 1;
      }

      // Retirer l'élément de sa position actuelle
      const [element] = arr.splice(index, 1);

      // Réinsérer l'élément à la nouvelle position
      arr.splice(newIndex, 0, element);

      return arr;
    },
    /*sleep(milliseconds) {
      return new Promise((resolve) => setTimeout(resolve, milliseconds));
    },
    async load() {
      this.isUploading = true
      let i=0
      for (let i = 1; i <= 100; i++) {
        await this.sleep(250)
        this.percentProgress+=1
      }
    },*/
    switchHelpScoutText() {
      if (this.$route.name !== 'pathManagement') {
        return;
      }
      switch (this.counter) {
        case 0:
          window.Beacon('config', {
            display: {
              style: 'iconAndText',
              text: this.articlesTitles[0][this.locale],
              iconImage: 'buoy',
            },
          });
          this.counter++;
          setTimeout(this.switchHelpScoutText, 5000);
          break;
        case 1:
          window.Beacon('config', {
            display: {
              style: 'iconAndText',
              text: this.articlesTitles[1][this.locale],
              iconImage: 'buoy',
            },
          });
          this.counter++;
          setTimeout(this.switchHelpScoutText, 5000);
          break;
        case 2:
          window.Beacon('config', {
            display: {
              style: 'iconAndText',
              text: this.articlesTitles[2][this.locale],
              iconImage: 'buoy',
            },
          });
          this.counter = 0;
          setTimeout(this.switchHelpScoutText, 5000);
          break;
      }
    },
    // on peut pas faire autrement?
    async setPathValues() {
      if (this.pathInformations.isRealtime) {
        try {
          await this.$store.dispatch('players/SUBSCRIBE', {
            pathId: this.pathId,
            kuzzle: this.$kuzzle,
            index: this.$store.getters['auth/currentUserIndex'],
          });
        } catch (error) {
          console.error(error.message);
        }
      }
      // tmp for migration
      if (!this.pathInformations.langsEdited) {
        this.pathInformations.langsEdited = [this.langEdition];
        const path = { langsEdited: [this.langEdition] };
        await this.updatePath(path);
      }
      if (!this.pathInformations.langsPublished) {
        if (this.pathInformations.isPublished) {
          this.pathInformations.langsPublished = ['fr'];
        } else {
          this.pathInformations.langsPublished = [];
        }
        const path = { langsPublished: this.pathInformations.langsPublished };
        await this.updatePath(path);
      }
      if (!this.pathInformations.introduction) {
        this.pathInformations.introductionn = { fr: '', en: '' };
      }
      if (!this.pathInformations.conclusion) {
        this.pathInformations.conclusion = { fr: '', en: '' };
      }
      this.pathInformations.langsEdited.forEach((lang) => {
        this.pathIntroduction[lang] = this.pathInformations.introduction[lang];
        this.pathConclusion[lang] = this.pathInformations.conclusion[lang];
      });
      if (!this.pathInformations.introductionMedia) {
        this.pathInformations.introductionMedia = {
          type: 'audio',
          url: '',
          name: '',
        };
      }
      if (!this.pathInformations.conclusionMedia) {
        this.pathInformations.conclusionMedia = {
          type: 'audio',
          url: '',
          name: '',
        };
      }
      this.pathMediaIntroduction = this.pathInformations.introductionMedia;
      this.pathMediaConclusion = this.pathInformations.conclusionMedia;
      this.pathIsPublished = this.pathInformations.isPublished;
    },
    getQrcodeUrl() {
      // prettier-ignore
      return this.pathId.length
        ? `https://api.qrserver.com/v1/create-qr-code/?size=450x450&data=https://explorama.app/play/${this.pathId}`
        : '';
    },
    getRequiredRule() {
      return [(data) => Boolean(data) || this.$t('pathForm.errors.required')];
    },
    getIntroductionRules() {
      return [
        ...this.getRequiredRule(),
        (pathIntroduction) =>
          (pathIntroduction && pathIntroduction.length <= this.maxCharIntro) ||
          this.$t('pathManagement.errors.introductionTooLong'),
      ];
    },
    getConclusionRules() {
      return [
        ...this.getRequiredRule(),
        (pathConclusion) =>
          (pathConclusion && pathConclusion.length <= this.maxCharIntro) ||
          this.$t('pathManagement.errors.conclusionTooLong'),
      ];
    },
    dragButtonClick() {
      this.isDragButtonClicked = true;
    },
    startDrag() {
      this.drag = true;
    },
    async approvePath() {
      const pathPayload = { isApproved: true };
      await this.updatePath(pathPayload);
    },
    async copyMission(pathId, missionId) {
      const body = {
        indexA: 'clients',
        idDocA: missionId,
        indexB: 'clients',
      };
      if (this.currentUser.loggedAsUser !== '') {
        body.user = this.currentUser.content.kuid;
      }
      try {
        const res = await this.$kuzzle.query({
          controller: 'duplicate',
          action: 'mission',
          body: body,
        });
        return res;
      } catch (e) {
        console.error(e.message);
        this.$toast.show(e);
      }
    },
    async copyMissionToPath(pathId, missionId) {
      const res = await this.copyMission(pathId, missionId);
      const newMissionId = res.result._id;
      const originalMissionId = missionId;
      const originalPathId = this.pathId;
      const missionsIds = [newMissionId];
      try {
        this.$store.dispatch('paths/ADD_MISSIONS', {
          kuzzle: this.$kuzzle,
          index: this.$store.getters['auth/currentUserIndex'],
          pathId: pathId,
          missionsIds,
          isCopying: true,
          originalPathId,
          originalMissionId,
        });
      } catch (e) {
        console.error(e.message);
        this.$toast.show(e);
      }
    },
    endDrag() {
      this.drag = false;
      this.isDragButtonClicked = false;
    },
    async updateIntroduction() {
      // condition ?
      if (
        this.pathIntroduction &&
        this.pathIntroduction[this.langEdition] &&
        (this.pathIntroduction[this.langEdition].length > this.maxCharIntro ||
          this.pathIntroduction[this.langEdition] ===
            this.pathInformations.introduction[this.langEdition])
      ) {
        return;
      }
      this.$store.commit('app/SET_LOADING');
      const path = {
        introduction: {
          [this.langEdition]: this.pathIntroduction[this.langEdition],
        },
      };
      await this.updatePath(path);
    },
    async updateIntroductionMedia(payload) {
      this.$store.commit('app/SET_LOADING');
      const path = {
        introductionMedia: {
          url: payload.url,
          name: payload.name,
          type: payload.type,
        },
      };
      await this.updatePath(path);
    },
    async updateConclusionMedia(payload) {
      this.$store.commit('app/SET_LOADING');
      const path = {
        conclusionMedia: {
          url: payload.url,
          name: payload.name,
          type: payload.type,
        },
      };
      await this.updatePath(path);
    },
    async updateConclusion() {
      //condition?
      if (
        this.pathConclusion &&
        this.pathConclusion[this.langEdition] &&
        (this.pathConclusion[this.langEdition].length > this.maxCharIntro ||
          this.pathConclusion[this.langEdition] ===
            this.pathInformations.conclusion[this.langEdition])
      ) {
        return;
      }
      this.$store.commit('app/SET_LOADING');
      const path = {
        conclusion: {
          [this.langEdition]: this.pathConclusion[this.langEdition],
        },
      };
      await this.updatePath(path);
    },
    async updatePath(payload) {
      try {
        const updated = await this.$store.dispatch('paths/UPDATE_PATH', {
          pathPayload: payload,
          index: this.$store.getters['auth/currentUserIndex'],
          pathId: this.pathId,
          kuzzle: this.$kuzzle,
        });
        if (updated.statDocExist === false) this.createStatDoc()
      } catch (e) {
        console.log(e.message);
        this.$toast.show(e.message);
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    async createStatDoc() {
      try {
        const updated = await this.$store.dispatch('stats/CREATE_STAT_DOC', {
          index: this.$store.getters['auth/currentUserIndex'],
          pathId: this.pathId,
          kuzzle: this.$kuzzle
        });
      } catch (e) {
        console.log(e.message);
        this.$toast.show(e.message);
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    async removeMission(payload) {
      this.$store.commit('app/SET_LOADING');
      try {
        await this.$store.dispatch('paths/REMOVE_MISSION', {
          kuzzle: this.$kuzzle,
          index: this.$store.getters['auth/currentUserIndex'],
          pathId: this.pathId,
          missionId: payload.missionId,
          indexKey: payload.indexKey,
        });
      } catch (error) {
        console.error(error.message);
        this.$toast.show(
          this.$t('pathManagement.errors.removeMission'),
          this.$t('common.error')
        );
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    getRawMission(missionId) {
      return this.$store.getters['missions/getMission'](missionId);
    },
    async openMissionPicker() {
      console.log('open mission picker');
      this.isMissionPickerOpen = true;

      // remove missing missions in path when open mission picker
      if (this.missingMissionsLength !== 0) {
        await this.asyncForEach(this.missingMissions, async (missionId) => {
          await this.removeMission({ missionId: missionId });
        });
      }
    },
    closeMissionPicker() {
      this.isMissionPickerOpen = false;
    },
    async addMissions() {
      this.isMissionPickerOpen = false;
      console.log('missionsToAdd', this.missionsToAdd);

      // copy private missions, get new missions ids
      const missionsIds = await Promise.all(
        this.missionsToAdd.map(async (missionId) => {
          let missionInfos =
            this.$store.getters['missions/getMissionById'](missionId);
          if (missionInfos.isPublic === Boolean(false)) {
            //copie uniquement des missions privées
            const res = await this.copyMission(this.pathId, missionId);
            console.log('copy result', res.result);
            return res.result._id;
          } else return missionId;
        })
      );
      console.log('missionsIds', missionsIds);

      if (this.pathInformations.langsEdited.length > 1) {
        //traduction des missions ajoutées si autre langue que locale dans le parcours
        const listMissionsToTranslateIds = missionsIds.filter(missionId => this.$store.getters['missions/getMissionById'](missionId).isPublic === Boolean(false))
        if (listMissionsToTranslateIds.length > 0) {
          let langs = this.pathInformations.langsEdited.filter(
            (langEdited) => langEdited !== this.langEdition
          );
          this.translateMissions(langs, listMissionsToTranslateIds);
        }
      }
      this.$store.commit('app/SET_LOADING');
      try {
        const res = await this.$store.dispatch('paths/ADD_MISSIONS', {
          kuzzle: this.$kuzzle,
          index: this.$store.getters['auth/currentUserIndex'],
          pathId: this.pathId,
          missionsIds,
          isCopying: false,
        });

        const missionsWithGeolocation = this.missions.filter(
          (mission) => missionsIds.includes(mission.key) && mission.geolocation
        );

        if (!missionsWithGeolocation || missionsWithGeolocation.length === 0) {
          this.$store.commit('app/UNSET_LOADING');
          return;
        }

        await this.asyncForEach(missionsWithGeolocation, async (mission) => {
          await this.editMissionLocation(mission.key, mission.geolocation);
        });
        this.$store.commit('app/UNSET_LOADING');
      } catch (error) {
        console.error(error.message);
        this.$toast.show(
          this.$t('pathManagement.errors.addMission'),
          this.$t('common.error')
        );
        this.$store.commit('app/UNSET_LOADING');
      }
    },
    async newMission() {
      const now = new Date();
      const mission = {
        name: { fr: '', en: '' },
        isPublic: false,
        isComplete: { fr: false, en: false },
        dateStart: Math.round(now.getTime() / 1000),
        dateEnd: Math.round(now.setFullYear(now.getFullYear() + 1) / 1000),
        games: [],
      };
      try {
        //this.$store.commit('app/SET_LOADING');
        let newMission = await this.$store.dispatch('missions/CREATE_MISSION', {
          newMissionPayload: mission,
          index: this.$store.getters['auth/currentUserIndex'],
          kuzzle: this.$kuzzle,
        });
        const missionId = newMission._id;

        this.$router.push(`/editMission/${this.pathId}/${missionId}`);

        let missionsIds = [missionId];
        const res = await this.$store.dispatch('paths/ADD_MISSIONS', {
          kuzzle: this.$kuzzle,
          index: this.$store.getters['auth/currentUserIndex'],
          pathId: this.pathId,
          missionsIds,
          isCopying: false,
        });
      } catch (error) {
        console.log('error', error);
        this.$toast.show(error.toString());
      }
    },
    async editMissionLocation(missionId, newLocation) {
      await this.$store.commit('app/SET_LOADING');
      try {
        await this.$store.dispatch('paths/UPDATE_MISSION', {
          kuzzle: this.$kuzzle,
          index: this.$store.getters['auth/currentUserIndex'],
          pathId: this.pathId,
          missionId,
          payload: {
            geolocationInPath: newLocation,
          },
        });
      } catch (error) {
        console.error(error.message);
        this.$toast.show(
          this.$t('pathForm.errors.update'),
          this.$t('missionCard.locationPickerModalTitle')
        );
      }
      await this.$store.commit('app/UNSET_LOADING');
    },
    async asyncForEach(array, callback) {
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
      }
    },
    async updateTranslatedText(textTranslated, type) {
      if (textTranslated) {
        if (type === 'introduction') {
          this.pathIntroduction[this.langEdition] = textTranslated;
          await this.updateIntroduction();
        } else if (type === 'conclusion') {
          this.pathConclusion[this.langEdition] = textTranslated;
          await this.updateConclusion();
        }
      }
    },
    async deletePathLang(lang) {
      let newLangsEdited = this.pathInformations.langsEdited;
      newLangsEdited = newLangsEdited.filter(
        (langEdited) => langEdited !== lang
      );

      if (newLangsEdited.length > 0) {
        let pathPayload = { langsEdited: newLangsEdited };
        if (
          this.pathInformations.langsPublished &&
          this.pathInformations.langsPublished.includes(lang)
        ) {
          //suppression de la langue publiée si publiée
          let newLangsPublished = this.pathInformations.langsPublished;
          newLangsPublished = newLangsPublished.filter(
            (langPublished) => langPublished !== lang
          );
          pathPayload.langsPublished = newLangsPublished;
          if (newLangsPublished.length === 0) {
            //suppression de la publicatio si c'etait la seule langue publiée
            pathPayload.isPublished = false;
          }
        }
        await this.updatePath(pathPayload);
        this.setPathValues();
      }
    },
    async addLangToPath(langs) {
      //utile?
      if (!langs || langs.length === 0) {
        return;
      }
      await this.translatePath(langs);

      if (this.pathInformations.missions.length > 0) {
        let listMissionsToTranslateIds = [];
        this.pathInformations.missions.forEach((mission) => {
          let missionInfos = this.$store.getters['missions/getMissionById'](
            mission.missionId
          );
          if (missionInfos.isPublic === Boolean(false)) {
            listMissionsToTranslateIds = [
              ...listMissionsToTranslateIds,
              mission.missionId,
            ];
          }
        });
        if (listMissionsToTranslateIds.length === 0) {
          return;
        }
        await this.translateMissions(langs, listMissionsToTranslateIds);
        langs.forEach((l) => {
          this.checkTranslationIsComplete(l);
        });
      }
    },
    checkTranslationIsComplete(lang) {
      let missionList = this.$store.getters['missions/getMissionsByIds'](
        this.idsMissionsInPath
      );
      // do not check translation for public missions
      missionList = Object.fromEntries(
        Object.entries(missionList).filter(([k, v]) => !v.isPublic)
      );
      const missionsNames = Object.values(missionList).map((m) => m.name);
      const missionsGames = Object.values(missionList).map((m) => m.games);
      const checkMissionsNames = missionsNames.map((n) => {
        return this.compareLangsValues(
          n,
          lang,
          this.originLangBeforeTranslation
        );
      });
      const gameList = missionsGames.map((mGameId) => {
        return this.$store.getters['missions/getGamesByIds'](mGameId);
      });

      let isMissionValidList = gameList.map((missionGames, index) => {
        if (!checkMissionsNames[index]) {
          return false;
        }
        let isMissionValid = false;
        const missionValid = Object.values(missionGames).map((game) => {
          let isGameValid = false;
          const jsp = Object.values(game).map((val) => {
            return this.compareLangsValues(
              val,
              lang,
              this.originLangBeforeTranslation
            );
          });
          // JCS la boucle doit s'arreter si le jeu n'est pas valide
          if (jsp.every((v) => v === true)) {
            isGameValid = true;
          }
          return isGameValid;
        });
        if (missionValid.every((v) => v === true)) {
          isMissionValid = true;
        }
        return isMissionValid;
      });

      Object.keys(missionList).forEach((id, index) => {
        const missionPayload = {
          isComplete: {
            [lang]: isMissionValidList[index],
          },
        };
        this.saveMissionWithoutLoad(id, missionPayload);
      });
    },
    isObject(value) {
      return Boolean(
        value && typeof value === 'object' && !Array.isArray(value)
      );
    },
    uploadingMedia(isUploading, percentProgress) {
      this.isUploading = isUploading;
      this.percentProgress = percentProgress;
    },
    compareLangsValues(obj, lang, originLang) {
      let res = null;
      if (this.isObject(obj)) {
        // SI OBJET
        if (lang in obj && originLang in obj) {
          // si on trouve les keys 'fr' & la langue traduite, alors verif
          const keyOfThis = Object.keys(obj);
          if (keyOfThis.includes(lang) && keyOfThis.includes(originLang)) {
            if (obj[originLang].length > 0 && obj[lang].length === 0) {
              // si text en fr mais pas en langue traduite, return false le jeu est incomplet
              return false;
            } else if (obj[originLang].length > 0 && obj[lang].length > 0) {
              // si text en fr & en langue traduite, return true
              return true;
            }
          }
        } // sinon, appel de la fonction avec toutes les keys de l'objet
        else {
          res = Object.values(obj).map((v) =>
            this.compareLangsValues(v, lang, originLang)
          );
        }
      } else if (Array.isArray(obj)) {
        // sinon si array, appel de la fonction avec toutes les cells
        res = obj.map((cell) =>
          this.compareLangsValues(cell, lang, originLang)
        );
      }
      if (!res) {
        return true;
      } // si res n'existe pas, alors obj est une string du style gameId ou type, return true, pas de vérif à faire
      if (!res.includes(false)) {
        // si res ne contient pas de false, alors aucune erreur de trad detectée
        return true;
      }
      return false; // sinon return false, le jeu est incomplet
    },
    async saveMissionWithoutLoad(missionId, missionPayLoad) {
      try {
        await this.$store.dispatch('missions/UPDATE_MISSION', {
          missionId: missionId,
          missionPayload: missionPayLoad,
          index: this.$store.getters['auth/currentUserIndex'],
          kuzzle: this.$kuzzle,
        });
      } catch (error) {
        console.log(error);
        this.$toast.show(this.$t('missionForm.name'));
      }
    },
    async translatePath(langs) {
      let newLangsEdited = this.pathInformations.langsEdited;
      this.$store.commit('app/SET_LOADING');
      await this.asyncForEach(langs, async (lang) => {
        if (
          this.pathInformations.langsEdited &&
          !this.pathInformations.langsEdited.includes(lang)
        ) {
          try {
            await this.$store.dispatch('paths/TRANSLATE_PATH', {
              index: this.$store.getters['auth/currentUserIndex'],
              pathId: this.pathId,
              originLanguage: this.locale,
              targetLanguage: lang,
              kuzzle: this.$kuzzle,
            });
            newLangsEdited = [...newLangsEdited, lang];
          } catch (error) {
            console.error(error.message);
          }
          this.$store.commit('app/UNSET_LOADING');
        }
      });

      let pathPayload = { langsEdited: newLangsEdited };
      await this.updatePath(pathPayload);
      this.setPathValues();
    },
    async translateMissions(langs, listMissionsIds) {
      console.log('translateMissions', langs, this.langEdition)
      if (listMissionsIds && listMissionsIds.length > 0) {
        await this.asyncForEach(langs, async (lang) => {
          let originLanguage = this.langEdition
          if (this.locale !== this.langEdition) originLanguage = this.locale
          if (lang === originLanguage ) return;

          this.$store.commit('app/SET_LOADING');
          try {
            await this.$store.dispatch('missions/TRANSLATE_MISSIONS', {
              index: this.$store.getters['auth/currentUserIndex'],
              listMissionsIds: listMissionsIds,
              originLanguage: this.locale,
              targetLanguage: lang,
              kuzzle: this.$kuzzle,
            });
          } catch (error) {
            console.error(error.message);
          }
          this.$store.commit('app/UNSET_LOADING');
        });
      }
    },
    updateMissionsToAdd(listMissions) {
      this.missionsToAdd = listMissions;
    },
    updateValidPathForm(value) {
      this.isValidPathForm = value;
    },
    changeLangEdition(newLang) {
      this.originLangBeforeTranslation = this.langEdition;
      if (this.langEdition !== newLang) {
        this.langEdition = newLang;
      }
    },
    changeHint() {
      this.showHint = !this.showHint;
    },
    async addLangPublished(lang) {
      const now = new Date();
      let langsPublished = this.pathInformations.langsPublished;
      //let promoCode={publicationDate: Math.round(now.getTime() / 1000), expirationDate: Math.round(now.getTime().addDays(this.pathInformations.promoCode.duration))}
      if (!langsPublished) {
        langsPublished = [];
      }
      if (!langsPublished.includes(lang)) {
        langsPublished = [...langsPublished, lang];
      }

      const pathPayload = { langsPublished: langsPublished, isPublished: true }; //, promoCode: promoCode };
      await this.updatePath(pathPayload);
    },
    async unpublishLang(lang) {
      let langsPublished = this.pathInformations.langsPublished;
      if (!langsPublished) {
        langsPublished = [];
      }
      let newLangsPublished = langsPublished.filter(
        (langPublished) => langPublished !== lang
      );
      let pathPayload = { langsPublished: newLangsPublished };
      if (newLangsPublished.length === 0) {
        //plus de langue publiée
        pathPayload.isPublished = false;
      }
      this.$store.commit('app/SET_LOADING');
      await this.updatePath(pathPayload);
    },
  },
};
</script>

<style lang="sass" scoped>

.cardWidth
  width: 600px

.alignBtn
  margin-bottom: 8px

.btnWidth
  width: 180px

.scrollContainer
  height: 76vh

.breakParagraph
  white-space: pre-line
  overflow: hidden
  display: -webkit-box
  -webkit-line-clamp: 5
  -webkit-box-orient: vertical

.containerSize
  height: 90vh

.rounded-card
    border-radius: 10px

.clickable
  cursor: pointer

.bgc-grey
  background-color: #ececec

.theme--light.v-list
  background-color: #ececec

.qrcode-image
  width: 200px

.qrcode-small
  width: 150px

.btnHeight
  height: 28px !important;

.addCard
  margin-top: 10px;
  margin-bottom: 20px;
  width: 300px

.disable-events
  pointer-events: none

.stickyClass
  position: -webkit-sticky;
  position: sticky;
  top: 100px;
  width: 130px;
  height: 0px;
  z-index: 100

.hintInfo
  border: 2px dashed #009999
  border-radius: 4px
  display: flex
  justify-content: center
</style>
